import _ from 'lodash';
import { pushStateLocationPlugin, servicesPlugin, UIRouterReact } from '@uirouter/react';
import { AUTH_ERRORS } from '~/modules/services/auth-service';
import { forgetLinkToken, isCredentialStateUrl, revealLinkToken } from '~/modules/pixwel/link-token';

const BOOLEAN_TYPE = {
	name: 'boolean',
	encode: (val) => val ? 'true' : 'false',
	decode: (val) => val === 'true',
	is: (val) => _.isBoolean(val)
};

const LIST_TYPE = {
	name: 'list',
	raw: true,
	encode: (items) => {
		if( !_.isArray(items) ) {
			return '';
		}

		return items.join(',');
	},
	decode: (item) => {
		if( !_.isString(item ) ) {
			return [];
		}
		if( item.includes(',') ) {
			return item.split(',');
		}
		else if( item.includes('%2C') ) {
			return item.split('%2C');
		}
		return [item];
	},
	is: (val) => {
		return _.isString(val) || _.isArray(val);
	},
	equals: (a, b) => {
		return _.isEqual(a, b);
	}
};

const AUTH_REJECTION_MESSAGES = Object.values(AUTH_ERRORS);

/**
 * Check whether a transition rejection is caused by an expected auth error
 * (e.g. missing token on first load). These are handled by the login flow,
 * so logging them only creates noise.
 */
export function isAuthRejection(error) {
	const detail = error?.detail;
	if(!detail) {
		return false;
	}

	// detail can be an Error object or a string
	const message = detail?.message ?? detail;
	if(typeof message === 'string' && AUTH_REJECTION_MESSAGES.includes(message)) {
		return true;
	}

	// Also suppress 401 HTTP errors that bubble through resolves
	const status = detail?.status ?? detail?.response?.status;
	return status === 401;
}

const uiRouter = new UIRouterReact();
uiRouter.plugin(servicesPlugin); // services plugins is necessary for the router to function
uiRouter.plugin(pushStateLocationPlugin);

// A bearer token that `hide-link-token` moved out of the address bar is for the route it came
// from; once the router has left that route it is spent or abandoned, and either way it should
// not outlive the visit in the tab's storage. See `link-token.js`.
uiRouter.transitionService.onSuccess({}, (transition) => {
	if(!isCredentialStateUrl(transition.to()?.url)) {
		forgetLinkToken();
	}
});
uiRouter.urlService.config.type('boolean', BOOLEAN_TYPE);
uiRouter.urlService.config.type('list', LIST_TYPE);
// to track transitions
// uiRouter.trace.enable('TRANSITION');
// uiRouter.urlService.rules.initial({ state: 'projects' });
uiRouter.urlService.rules.otherwise('/');

// Override the default error handler to suppress expected auth-related
// transition rejections that fill the console on every unauthenticated load.
uiRouter.stateService.defaultErrorHandler((error) => {
	if(isAuthRejection(error)) {
		return; // silently handled by the login flow
	}
	console.error('Transition Rejection:', error);
});

const RouteService = {
	_lastState: null,
	_isStarted: false,

	get lastState() {
		return RouteService._lastState;
	},

	get currentState() {
		return uiRouter.stateService.current;
	},

	registerStates(states) {
		states.forEach((state) => {
			try {
				uiRouter.stateRegistry.register(state);
			}
			catch(e) {
				console.error(e);
			}
		});

		return uiRouter;
	},

	getRouteInfo() {
		return {
			name: uiRouter.stateService.current.name,
			params: uiRouter.stateService.params
		};
	},

	/**
	 * A route parameter -- with a bearer token that `hide-link-token` moved out of the address
	 * bar handed back in place of its placeholder, so the page that spends it need not know.
	 * Only for the parameter that carried it, on the route it came from: any other parameter
	 * whose value is the placeholder word is just that word. See `link-token.js`.
	 */
	getRouteParam(param) {
		return revealLinkToken(uiRouter.stateService.params[param], {
			param,
			pathname: window.location.pathname
		});
	},

	getRouteParams() {
		const { pathname } = window.location;

		return _.mapValues(uiRouter.stateService.params, (value, param) => (
			revealLinkToken(value, { param, pathname })
		));
	},

	setRouteParams(params) {
		setTimeout(() => uiRouter.stateService.go(uiRouter.globals.$current.name, params, { location: 'replace', inherit: false }));
	},

	updateRouteParams(params) {
		setTimeout(() => uiRouter.stateService.go(uiRouter.globals.$current.name, params, { location: 'replace', inherit: true }));
	},

	/**
	 * Like `updateRouteParams`, but as a new history entry rather than rewriting the current one.
	 *
	 * For a parameter that opens something the person will expect Back to close again -- a
	 * panel, a tab -- `replace` leaves nothing for Back to return to, so it leaves the page
	 * instead.
	 */
	pushRouteParams(params) {
		setTimeout(() => uiRouter.stateService.go(
			uiRouter.globals.$current.name, params, { location: true, inherit: true }
		));
	},

	goBack() {
		window.history.back();
	},

	reload() {
		return uiRouter.stateService.reload();
	},

	navigateTo(state, params, reload = false, useTimeout = true) {
		return new Promise((resolve, reject) => {
			const useTimeoutWrapper = (fn) => useTimeout ? setTimeout(fn) : fn();
			return useTimeoutWrapper(() => {
				uiRouter.stateService.go(state, params, { reload, reloadState: true })
					.then(resolve)
					.catch(reject);
			});
		});
	},

	hardNavigateTo(state, ...params) {
		window.location = uiRouter.stateService.href(state, ...params);
	},
	hardNavigateToBlank(state, params) {
		let url = uiRouter.stateService.href(state, params);
		window.open(url, '_blank');
	},

	/**
	 * Open a state in a standalone popup window the user can move and resize
	 * independently of the main window. Passing a window-features string with
	 * explicit dimensions makes browsers render a separate popup rather than a
	 * new tab. The popup is centered over the current window.
	 *
	 * @param {string} state    Router state name.
	 * @param {object} params   State params.
	 * @param {object} [options]
	 * @param {number} [options.width=1024]
	 * @param {number} [options.height=768]
	 * @param {string} [options.name='_blank'] Window target name; reuse the same
	 *   name to focus an existing popup instead of opening another.
	 * @returns {Window|null} The opened window, or null if blocked.
	 */
	hardNavigateToPopup(state, params, { width = 1024, height = 768, name = '_blank' } = {}) {
		const url = uiRouter.stateService.href(state, params);

		// Never open larger than the available screen (e.g. a tall portrait
		// document window on a short laptop display).
		width = Math.min(width, window.screen?.availWidth ?? width);
		height = Math.min(height, window.screen?.availHeight ?? height);

		// Center over the current window, accounting for multi-monitor setups
		// where screenLeft/screenTop can be negative or offset.
		const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
		const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
		const outerWidth = window.outerWidth ?? document.documentElement.clientWidth ?? width;
		const outerHeight = window.outerHeight ?? document.documentElement.clientHeight ?? height;
		const left = dualScreenLeft + Math.max(0, (outerWidth - width) / 2);
		const top = dualScreenTop + Math.max(0, (outerHeight - height) / 2);

		const features = [
			'popup=yes',
			`width=${width}`,
			`height=${height}`,
			`left=${Math.round(left)}`,
			`top=${Math.round(top)}`,
			'resizable=yes',
			'scrollbars=yes'
		].join(',');

		const popup = window.open(url, name, features);
		popup?.focus();
		return popup;
	},

	navigateTo2x(fragment) {
		let baseUrl = (window.pixwel?.env === 'production')
			? 'https://platform.pixwel.com'
			: 'https://staging.pixwel.com';
		window.open(`${baseUrl}/${fragment}`);
	},

	hardNavigateTo2x(url) {
		window.open(url);
	},

	registerRouteInterceptor(interceptor) {
		return uiRouter.transitionService.onStart({}, interceptor );
	},

	stateHref(state, ...params) {
		return uiRouter.stateService.href(state, ...params);
	},

	/**
	 * Normalize the current origin to the canonical platform host.
	 * Handles beta.* / edge.* / 2x.* → platform.* rewrites so that
	 * externally-shared URLs always point at the correct environment.
	 *
	 * Shares the beta/edge mappings with `PublicUrl::canonical()` on the API,
	 * but also retains the existing frontend-only 2x.* rewrite.
	 * This handles links the API never sees — copy-to-clipboard.
	 *
	 * @returns {string} The normalized origin (e.g. "https://platform.pixwel.com")
	 */
	normalizeOrigin() {
		return window.location.origin
			.replace('edge.', 'platform.')
			.replace('beta.', 'platform.')
			.replace('2x.', 'platform.');
	},

	clearStates() {
		RouteService._lastState = null;
		RouteService._isStarted = false;
	},

	isAuthRejection
};

export default RouteService;
